/**
 * AdjustStrike (Foundry: rule-element/adjust-strike.ts): add or remove a trait, action trait,
 * material effect or property rune on the strikes a definition matches, or change their range.
 */
import { isValidPredicate, testPredicate, type RawPredicate } from "../predicate";
import { cleanRuleValue, resolveInjectedProperties, resolveValue } from "../resolve";
import { sluggifyCamel } from "../slug";
import type { EngineState } from "../state";
import type { StrikeAdjustment } from "../synthetics";
import { MATERIAL_DAMAGE_EFFECTS } from "../values";
import { addOrUpgradeTrait, removeTrait, type EngineWeapon } from "../weapons";
import { AE_LIKE_MODES, getNewValue, ChangeFailure, type AELikeMode } from "./ae-like";
import type { RuleHandler, RuleInstance } from "./base";

const PROPERTIES = ["materials", "property-runes", "range-increment", "traits", "weapon-traits"] as const;
type Property = (typeof PROPERTIES)[number];

interface AdjustStrikeData {
  mode: AELikeMode;
  property: Property;
  definition: RawPredicate;
  value: unknown;
}

function parse(rule: RuleInstance): void {
  const s = rule.source;
  if (!(AE_LIKE_MODES as readonly string[]).includes(String(s.mode))) return rule.fail(`mode: "${String(s.mode)}" is not a change mode`);
  if (!(PROPERTIES as readonly string[]).includes(String(s.property))) return rule.fail(`property: "${String(s.property)}" is not one of ${PROPERTIES.join(", ")}`);
  if (s.definition !== undefined && !isValidPredicate(s.definition)) return rule.fail("definition: must be a predicate");
  const value = cleanRuleValue(s.value);
  if (value === undefined) return rule.fail("value: may not be undefined");
  rule.priority = 110;
  rule.data = { mode: s.mode as AELikeMode, property: s.property as Property, definition: (s.definition as RawPredicate | undefined) ?? [], value } as unknown as Record<string, unknown>;
}

function beforePrepareData(rule: RuleInstance, state: EngineState): void {
  if (!rule.test(state)) return;
  const data = rule.data as unknown as AdjustStrikeData;
  const change = resolveValue(rule, state, data.value);
  if (rule.ignored) return;
  const definition = resolveInjectedProperties(rule, state, structuredClone(data.definition), { warn: false });
  const matches = (weapon: EngineWeapon): boolean => definition.length === 0 || testPredicate(definition, new Set(weapon.getRollOptions("item")));
  const addRemove = ["add", "subtract", "remove"].includes(data.mode);
  const base = { source: rule.item.uuid, sourceName: rule.item.name };
  const adjustment: StrikeAdjustment = ((): StrikeAdjustment => {
    switch (data.property) {
      case "materials":
        return {
          ...base,
          adjustDamageRoll: (weapon, { materials }) => {
            if (!addRemove) return rule.fail('A strike adjustment of material effects must be used with the "add", "subtract", or "remove" mode.');
            if (!matches(weapon)) return;
            if (typeof change !== "string" || !(MATERIAL_DAMAGE_EFFECTS as readonly string[]).includes(change)) return rule.fail(`"${String(change)}" is not a supported weapon material effect.`);
            if (data.mode === "add") materials.add(change);
            else materials.delete(change);
          },
        };
      case "range-increment":
        return {
          ...base,
          adjustWeapon: (weapon) => {
            if (typeof change !== "number") return rule.fail("Change value is not a number.");
            if (!matches(weapon)) return;
            const increment = weapon.range?.increment;
            if (typeof increment !== "number") return rule.fail("A weapon that meets the definition lacks a range increment.");
            const next = getNewValue(data.mode, increment, change);
            if (next instanceof ChangeFailure) return rule.fail(next.message);
            weapon.system.range = Number(next);
          },
        };
      case "traits":
        return {
          ...base,
          adjustTraits: (weapon, traits) => {
            if (!addRemove) return rule.fail('A strike adjustment of traits must be used with the "add", "subtract", or "remove" mode.');
            const table = state.catalogue?.table("actionTraits");
            if (typeof change !== "string" || (table && !(change in table))) return rule.fail(`"${String(change)}" is not a recognized action trait.`);
            if (!matches(weapon)) return;
            if (data.mode === "add" && !traits.includes(change)) traits.push(change);
            else if (data.mode !== "add" && traits.includes(change)) traits.splice(traits.indexOf(change), 1);
          },
        };
      case "weapon-traits":
        return {
          ...base,
          adjustWeapon: (weapon) => {
            if (!addRemove) return rule.fail('A strike adjustment of weapon traits must be used with the "add", "subtract", or "remove" mode.');
            const known = state.catalogue?.table("weaponTraits");
            const knownNpc = state.catalogue?.table("npcAttackTraits");
            if (typeof change !== "string" || (known && !(change in known) && !(knownNpc && change in knownNpc))) return rule.fail(`"${String(change)}" is not a recognized weapon trait.`);
            if (!matches(weapon)) return;
            if (weapon.slug === "basic-unarmed" && /^(?:modular|versatile)/.test(change)) return;
            const damageType = weapon.damage.damageType;
            if (data.mode === "add" && [`modular-${damageType}`, `versatile-${damageType}`].includes(change)) return;
            const traits = weapon.system.traits!.value!;
            if (data.mode === "add") addOrUpgradeTrait(traits, change);
            else removeTrait(traits, change);
          },
        };
      case "property-runes":
        return {
          ...base,
          adjustWeapon: (weapon) => {
            if (!addRemove) return rule.fail('A strike adjustment of weapon property runes must be used with the "add", "subtract", or "remove" mode.');
            const runeSlug = sluggifyCamel(String(change));
            if (!runeSlug) return rule.fail(`"${String(change)}" is not a recognized weapon property rune.`);
            if (!matches(weapon)) return;
            const runes = weapon.system.runes!;
            const property = (runes.property ??= []);
            if (data.mode === "add") {
              if (!property.includes(runeSlug)) property.push(runeSlug);
            } else if (property.includes(runeSlug)) {
              property.splice(property.indexOf(runeSlug), 1);
            }
          },
        };
    }
  })();
  state.synthetics.strikeAdjustments.push(adjustment);
}

export const AdjustStrikeHandler: RuleHandler = { key: "AdjustStrike", parse, beforePrepareData };
